import { useEffect, useState } from "react";
import { Trash2, FileDown } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["en attente", "payée", "confirmée", "en préparation", "expédiée", "livrée", "annulée"];

const CARRIERS = [
  { key: "colissimo", label: "Colissimo" },
  { key: "chronopost", label: "Chronopost" },
  { key: "ups", label: "UPS" },
  { key: "dhl", label: "DHL" },
  { key: "mondial_relay", label: "Mondial Relay" },
];

function ExportButton({ onClick }) {
  return (
    <button onClick={onClick} data-testid="orders-export-csv"
      className="flex items-center gap-2 rounded-lg border border-orange-500/40 px-4 py-2 text-xs font-bold uppercase tracking-wider text-orange-400 transition-all hover:bg-orange-500/10">
      <FileDown className="h-4 w-4" /> Exporter CSV
    </button>
  );
}

export default function OrdersTab() {
  const [orders, setOrders] = useState([]);

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/orders/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "commandes-emisphere.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveTracking = async (id, status, tracking, carrier) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status, tracking, carrier });
      toast.success("Suivi enregistré");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cette commande ?")) return;
    try {
      await api.delete(`/admin/orders/${id}`);
      toast.success("Commande supprimée");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const refund = async (id) => {
    if (!window.confirm("Lancer le remboursement Stripe ? Le stock sera restauré.")) return;
    try {
      await api.post(`/admin/orders/${id}/refund`);
      toast.success("Remboursement effectué, stock restauré");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (orders.length === 0) {
    return (
      <div className="grid gap-4">
        <div className="flex justify-end">
          <ExportButton onClick={exportCsv} />
        </div>
        <p data-testid="orders-empty" className="text-slate-500">Aucune commande pour le moment.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4" data-testid="orders-list">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{orders.length} commande(s)</p>
        <ExportButton onClick={exportCsv} />
      </div>
      {orders.map((o) => {
        const orderItems = o.items?.length
          ? o.items
          : [{ product_name: o.product_name, size: o.size, quantity: o.quantity, flocage: o.flocage, pseudo: o.pseudo }];
        return (
        <div key={o.id} data-testid={`order-row-${o.id}`} className="card-tactical flex flex-wrap items-center gap-4 p-5">
          <div className="min-w-[180px] flex-1">
            <p className="font-heading text-lg font-bold uppercase">
              {orderItems.length > 1 ? `Panier — ${orderItems.length} articles` : o.product_name}
            </p>
            {orderItems.map((it, j) => (
              <p key={j} className="text-xs text-slate-400" data-testid={`order-item-${o.id}-${j}`}>
                {orderItems.length > 1 ? `${it.product_name} — ` : ""}Taille {it.size} × {it.quantity}
                {it.flocage && it.pseudo ? ` — Flocage : ${it.pseudo}` : ""}
              </p>
            ))}
            <p className="text-xs text-slate-400">
              {o.customer_name} — {o.customer_email}
            </p>
            <p className="text-xs text-slate-500">{o.customer_address}</p>
            {o.shipping && (
              <p className="mt-1 text-xs text-cyan-300">Livraison : {o.shipping.label} ({o.shipping.price.toFixed(2)} €) — {o.shipping.delay}</p>
            )}
            {o.promo && (
              <p className="mt-1 text-xs font-bold text-emerald-400">Code {o.promo.code} (-{o.promo.percent} %) : -{o.promo.discount.toFixed(2)} €</p>
            )}
          </div>
          <div className="text-sm text-slate-300">
            <p className="font-mono2 font-bold text-orange-500">{o.total.toFixed(2)} €</p>
            {o.payment_status && (
              <span className={`mt-1 inline-block rounded px-2 py-0.5 text-[10px] font-bold uppercase ${o.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-400" : "bg-orange-500/15 text-orange-400"}`}>
                {o.payment_status === "paid" ? "Payée" : "Paiement en attente"}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString("fr-FR")}</div>
          <div className="flex items-center gap-2">
            <Select value={o.carrier || ""} onValueChange={(v) => saveTracking(o.id, o.status, o.tracking || "", v)}>
              <SelectTrigger data-testid={`order-carrier-${o.id}`} className="w-36 bg-[#060B18] text-xs">
                <SelectValue placeholder="Transporteur" />
              </SelectTrigger>
              <SelectContent>
                {CARRIERS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <input
              placeholder="N° de suivi"
              defaultValue={o.tracking || ""}
              data-testid={`order-tracking-${o.id}`}
              id={`tracking-${o.id}`}
              className="w-36 rounded-lg border border-input bg-[#060B18] px-2 py-2 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <button
              onClick={() => saveTracking(o.id, o.status, document.getElementById(`tracking-${o.id}`).value, o.carrier || "")}
              data-testid={`order-tracking-save-${o.id}`}
              className="rounded-lg border border-cyan-400/40 px-3 py-2 text-xs font-bold uppercase tracking-wider text-cyan-300 transition-all hover:scale-[1.02] hover:bg-cyan-400/10"
            >
              Suivi
            </button>
          </div>
          <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
            <SelectTrigger data-testid={`order-status-${o.id}`} className="w-40 bg-[#060B18]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          {o.payment_status === "paid" && (
            <button onClick={() => refund(o.id)} data-testid={`order-refund-${o.id}`}
              className="rounded-lg border border-orange-500/40 px-3 py-2 text-xs font-bold uppercase tracking-wider text-orange-400 transition-all hover:scale-[1.02] hover:bg-orange-500/10">
              Rembourser
            </button>
          )}
          <button onClick={() => remove(o.id)} data-testid={`order-delete-${o.id}`} aria-label="Supprimer la commande"
            className="rounded-lg border border-red-500/40 p-2 text-red-400 transition-all hover:scale-[1.02] hover:bg-red-500/10">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        );
      })}
    </div>
  );
}
